import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  Login as LoginIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { MeetingRoom } from '../../types/models';
import RoomDetailModal from '../room/RoomDetailModal';
import { trackRoomSelection } from '../../services/analytics';
import logo from '../../assets/images/ksmartech_logo.png';
import { SUBSCRIBED_CALENDARS } from '../../constants/subscribedCalendars';

interface SidebarProps {
  rooms?: MeetingRoom[];
  /** 서버와 끊겨 예약을 만들 수 없는 상태 */
  bookingDisabled?: boolean;
  selectedRoomId: string | null;
  onRoomSelect: (roomId: string) => void;
  onNewBooking: () => void;
  onMyBookings: () => void;
  // onAppDownload: () => void;
  onUpcomingFeatures: () => void;
  onReleaseNote: () => void;
  onSubscribe: () => void;
  onCustomerService: () => void;
  isAuthenticated?: boolean;
  onLogout?: () => void;
  onLogin?: () => void;
  userName?: string;
  enabledCalendars?: string[];
  onCalendarToggle?: (calendarName: string) => void;
  setSnackbar?: (snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }) => void;
}

const DRAWER_WIDTH = 175;

const Sidebar: React.FC<SidebarProps> = ({ rooms = [], bookingDisabled = false, selectedRoomId, onRoomSelect, onNewBooking, onMyBookings, onUpcomingFeatures, onReleaseNote, onSubscribe, onCustomerService, isAuthenticated = false, onLogout, onLogin, userName, enabledCalendars = [], onCalendarToggle, setSnackbar }) => {
  // 모든 Hooks를 조건문 밖에서 선언
  const [isPwaMode, setIsPwaMode] = useState<boolean>(false);
  const [selectedRoom, setSelectedRoom] = useState<MeetingRoom | null>(null);
  const [roomDetailModalOpen, setRoomDetailModalOpen] = useState<boolean>(false);
  const [roomsOpen, setRoomsOpen] = useState(true);
  const [myBookingsOpen, setMyBookingsOpen] = useState(true);
  const [subscribedCalendarsOpen, setSubscribedCalendarsOpen] = useState(true);
  
  // 모바일 디바이스 감지 함수
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);
  
  // 모든 useEffect Hook을 조건문 이전에 선언
  // PWA 모드(standalone) 및 모바일 디바이스 감지
  useEffect(() => {
    // display-mode 미디어 쿼리로 standalone 모드 감지
    const pwaMediaQuery = window.matchMedia('(display-mode: standalone)');
    setIsPwaMode(pwaMediaQuery.matches);
    
    // 모바일 디바이스 감지 (화면 너비가 768px 미만일 때)
    const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    setIsMobileDevice(mobileMediaQuery.matches);
    
    // 변경 사항 감지를 위한 이벤트 리스너
    const handlePwaChange = (e: MediaQueryListEvent) => {
      setIsPwaMode(e.matches);
    };
    
    const handleMobileChange = (e: MediaQueryListEvent) => {
      setIsMobileDevice(e.matches);
    };
    
    pwaMediaQuery.addEventListener('change', handlePwaChange);
    mobileMediaQuery.addEventListener('change', handleMobileChange);
    
    return () => {
      pwaMediaQuery.removeEventListener('change', handlePwaChange);
      mobileMediaQuery.removeEventListener('change', handleMobileChange);
    };
  }, []);

  // 회의실 목록은 App이 이미 불러온 것을 전달받아 사용한다.
  // (각자 조회하면 같은 데이터를 페이지 로드마다 두 번 받게 된다)

  // 선택된 회의실 ID가 변경될 때 해당 회의실 정보 찾기
  useEffect(() => {
    // PWA 모드나 모바일에서는 회의실 정보를 찾을 필요가 없음
    if (!isPwaMode && !isMobileDevice && selectedRoomId) {
      const room = rooms.find(r => r.id === selectedRoomId);
      setSelectedRoom(room || null);
    } else {
      setSelectedRoom(null);
    }
  }, [selectedRoomId, rooms, isPwaMode, isMobileDevice]);
  
  // PWA 모드나 모바일에서는 사이드바를 렌더링하지 않음
  if (isPwaMode || isMobileDevice) {
    return null;
  }

  // 회의실 선택 핸들러
  const handleRoomSelect = (roomId: string) => {
    // 회의실 선택 시 해당 회의실 정보를 찾고 모달 표시
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      trackRoomSelection(room, null); // Firebase Analytics: 회의실 선택 추적
      setSelectedRoom(room);
      setRoomDetailModalOpen(true);
    }
    // 부모 컴포넌트에 알림
    onRoomSelect(roomId);
  };

  const handleCloseModal = () => {
    setRoomDetailModalOpen(false);
  };

  const handleRoomsToggle = () => {
    setRoomsOpen(!roomsOpen);
  };

  const handleMyBookingsToggle = () => {
    setMyBookingsOpen(!myBookingsOpen);
  };

  const handleSubscribedCalendarsToggle = () => {
    setSubscribedCalendarsOpen(!subscribedCalendarsOpen);
  };

  const handleCalendarToggle = (calendarName: string) => {
    onCalendarToggle?.(calendarName);
  };


  // PWA 모드가 아닐 때만 사이드바 렌더링
  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#f5f5f5',
          borderRight: '1px solid rgba(0,0,0,0.08)',
          pt: 2,
          display: 'flex',       // Flex 컨테이너로 설정
          flexDirection: 'column', // 아이템들을 세로로 정렬
          height: '100%',
        },
      }}
    >
      <Box sx={{ pt: 0.2, pl: 2, pr: 1.5 }}>
        <img src={logo} alt="Logo" style={{ height: '18px' }} />
      </Box>
      <Box sx={{ px: 1, py: 1 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
          onClick={onNewBooking}
          disabled={bookingDisabled}
          fullWidth
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            fontSize: '0.7rem',
            py: 0.7,
            px: 1.5,
            backgroundColor: '#fff',
            color: '#3c4043',
            border: '1px solid #dadce0',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#f6f9fe',
              boxShadow: 'none',
            },
          }}
        >
          예약하기
        </Button>
        {bookingDisabled && (
          <Box sx={{ mt: 0.6, fontSize: '0.65rem', color: '#80868b', textAlign: 'center' }}>
            연결되면 예약 가능
          </Box>
        )}
      </Box>

      <RoomDetailModal 
        open={roomDetailModalOpen} 
        onClose={handleCloseModal} 
        room={selectedRoom} 
      />

      <List sx={{ px: 0.5 }}>
        {/* 로그인한 사용자에게만 내 예약 메뉴 표시 */}
        {isAuthenticated && (
          <>
            <ListItemButton 
              onClick={handleMyBookingsToggle}
              sx={{
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#f6f9fe',
                },
              }}
            >
              <ListItemText
                primary="내 예약"
                primaryTypographyProps={{
                  sx: { fontSize: '0.75rem' }
                }}
              />
              {myBookingsOpen ? <ExpandLess sx={{ color: '#5f6368' }} /> : <ExpandMore sx={{ color: '#5f6368' }} />}
            </ListItemButton>

            <Collapse in={myBookingsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  onClick={onMyBookings}
                  sx={{
                    borderRadius: '4px',
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: '#f6f9fe',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                    }}
                  />
                  <ListItemText 
                    primary="예약 목록 보기"
                    primaryTypographyProps={{
                      sx: { fontSize: '0.7rem' }
                    }}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        <ListItemButton 
          onClick={handleRoomsToggle}
          sx={{
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          <ListItemText
            primary="회의실"
            primaryTypographyProps={{
              sx: { fontSize: '0.75rem' }
            }}
          />
          {roomsOpen ? <ExpandLess sx={{ color: '#5f6368' }} /> : <ExpandMore sx={{ color: '#5f6368' }} />}
        </ListItemButton>

        <Collapse in={roomsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {rooms.map((room) => (
              <ListItemButton
                key={room.id}
                selected={selectedRoomId === room.id}
                onClick={() => handleRoomSelect(room.id)}
                sx={{
                  borderRadius: '4px',
                  px: 2,
                  py: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: '#e8f0fe',
                    '&:hover': {
                      backgroundColor: '#e8f0fe',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#f6f9fe',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: room.color || '#cccccc',
                    mr: 1,
                    display: 'inline-block',
                  }}
                />
                <ListItemText
                  primary={room.name}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '0.7rem',
                      fontWeight: selectedRoomId === room.id ? 500 : 400,
                      color: '#3c4043',
                    },
                  }}
                />
              </ListItemButton>
            ))}

          </List>
        </Collapse>

        <ListItemButton
          onClick={handleSubscribedCalendarsToggle}
          sx={{
            borderRadius: '4px',
            mt: 1,
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          <ListItemText
            primary="구독한 캘린더"
            primaryTypographyProps={{
              sx: { fontSize: '0.75rem' }
            }}
          />
          {subscribedCalendarsOpen ? <ExpandLess sx={{ color: '#5f6368' }} /> : <ExpandMore sx={{ color: '#5f6368' }} />}
        </ListItemButton>

        <Collapse in={subscribedCalendarsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {SUBSCRIBED_CALENDARS.map((calendar) => (
              <ListItemButton
                key={calendar.id}
                onClick={() => handleCalendarToggle(calendar.name)}
                sx={{
                  borderRadius: '4px',
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: '#f6f9fe',
                  },
                }}
              >
                <Checkbox
                  edge="start"
                  checked={enabledCalendars.includes(calendar.name)}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    padding: '4px',
                    marginRight: '4px',
                    color: calendar.color,
                    '&.Mui-checked': {
                      color: calendar.color,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1rem',
                    },
                  }}
                />
                <ListItemText
                  primary={calendar.name}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '0.7rem',
                      color: '#3c4043',
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </List> 


      <Box sx={{ flexGrow: 1 }} />
      <List sx={{px: 1, pb: 2,}}>        
        
{/*         
        { <ListItemButton
          onClick={onAppDownload}
          sx={{
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          <ListItemText
            primary="앱 다운로드"
            primaryTypographyProps={{
              sx: { fontSize: '0.7rem' }
            }}
          />
        </ListItemButton> */}
{/*  
        <ListItemButton
          onClick={onUpcomingFeatures}
          sx={{
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          { <ListItemText
            primary="개발 예정 기능"
            primaryTypographyProps={{
              sx: { fontSize: '0.7rem' }
            }}
          />
        </ListItemButton>

        <ListItemButton
          onClick={onReleaseNote}
          sx={{
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                릴리즈 노트
                {showNewBadge && (
                  <Chip
                    label="N"
                    size="small"
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: '#fff',
                      fontWeight: 700,
                      width: 18,
                      height: 18, 
                      fontSize: '0.75rem',
                      padding: 0, // 모든 패딩을 0으로 설정
                      '.MuiChip-label': { // MuiChip-label 클래스에 직접 접근하여 패딩 조정
                        padding: 0,
                        lineHeight: 'normal', // 텍스트 라인 높이도 조절하여 수직 중앙 정렬에 도움
                      },
                    }}
                  />
                )}
              </Box>
            }
            primaryTypographyProps={{
              sx: { fontSize: '0.7rem' }
            }}
          />
        </ListItemButton>

        <ListItemButton
          onClick={handleSubscribeClick}
          sx={{
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          <ListItemText
            primary="구독하기"
            primaryTypographyProps={{
              sx: { fontSize: '0.7rem' }
            }}
          />
        </ListItemButton>

        <ListItemButton
          onClick={handleCustomerServiceClick}
          sx={{
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f6f9fe',
            },
          }}
        >
          <ListItemText
            primary="문의 & 개선 제안"
            primaryTypographyProps={{
              sx: { fontSize: '0.7rem' }
            }}
          />
        </ListItemButton> } 
         */}

        <Divider sx={{ my: 0.5 }} />

        {isAuthenticated ? (
          <ListItemButton
            onClick={(e) => {
              e.preventDefault();
              onLogout?.();
            }}
            sx={{
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#f6f9fe',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 0.5, color: '#5f6368', '& svg': { fontSize: '1rem' } }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary={userName}
              primaryTypographyProps={{
                sx: { fontSize: '0.875rem', color: '#3c4043' }
              }}
            />
          </ListItemButton>
        ) : (
          <ListItemButton
            onClick={onLogin}
            sx={{
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#f6f9fe',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 0.5, color: '#5f6368', '& svg': { fontSize: '1rem' } }}>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText
              primary="로그인"
              primaryTypographyProps={{
                sx: { fontSize: '0.875rem', color: '#3c4043' }
              }}
            />
          </ListItemButton>
        )}
        </List>
      
    </Drawer>
  );
};

export default Sidebar;

//github action 테스트를 위한 주석 커밋. 바로 삭제 예정.
